//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 30/06/2025
// Version       : 1
// Description   : Tabla con los tanques para autoeelevadores (capacidad de 0.04 m3)
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo, useRef } from "react";
import { UrlApi } from "../../../../../services/apirest";
import {
  MaterialReactTable,
  MRT_ToggleFiltersButton,
  MRT_FullScreenToggleButton,
} from "material-react-table";
// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";

import {
  Box,
  Container,
  Dialog,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme,
  ListItemIcon,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";

// ICONOS
import { Delete } from "@mui/icons-material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import BlockIcon from "@mui/icons-material/Block";
import PropaneIcon from "@mui/icons-material/Propane";

// Componentes
import DatosTanques from "../acciones/DatosTanques";
import Editartanque from "../acciones/Editartanque";
import BajaTanque from "../acciones/BajaTanque";

function Autoelevadores() {
  const [autoelevadores, setAutoelevadores] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tanque, setTanque] = useState({});
  const [baja, setBaja] = useState(false);
  const [hiddenmuestrainactivo, setHiddenmuestrainactivo] = useState(false);
  const [hiddenmuestraactivos, setHiddenmuestraactivos] = useState(true);

  // ===============================
  // CONTROLADOR DE PETICIONES FETCH
  // ===============================

  // CONTROLLER DE LA PETICIÓN ACTUAL
  // Esto permite abortar una request en curso antes de iniciar una nueva.
  const abortCtrl = useRef(null);

  // Función genérica que:
  // 1. Cancela cualquier petición previa (si existe).
  // 2. Crea un nuevo "AbortController" para la petición actual.
  // 3. Ajusta el estado de carga (isLoading) y la visibilidad de botones.
  // 4. Realiza el fetch y actualiza el estado con los datos recibidos.
  const fetchWithAbort = async (url, showActivos) => {
    // Abortar petición anterior si existe
    if (abortCtrl.current) {
      abortCtrl.current.abort();
    }

    // Crear nuevo controller y guardarlo
    const controller = new AbortController();
    abortCtrl.current = controller;

    // Ajustar estados de loading
    setIsLoading(true);
    setHiddenmuestraactivos(showActivos);
    setHiddenmuestrainactivo(!showActivos);

    try {
      // Llamada fetch con el signal para permitir abortar la request
      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json();

      // Si la petición se completa (sin abortar) se actualiza el estado
      setAutoelevadores(json);
    } catch (error) {
      // Si la petición fue abortada, ignoramos el error
      // Cualquier otro error (network, parseo, etc.) se muestra en consola
      if (error.name !== "AbortError") {
        console.error("Fetch error:", error);
      }
    } finally {
      // Al finalizar la última petición se termina el estado de "loading"
      setIsLoading(false);
    }
  };

  // ------------------------------------------------------------
  // Wrappers para cada endpoint:
  // "Activos" -> /autoelevadores
  // "Inactivos" -> /autoelevadores/inactivos
  // ------------------------------------------------------------
  const obtenerTanques = () =>
    fetchWithAbort(UrlApi + "/tanques/tabs/autoelevadores", true);

  const obtenerInactivos = () =>
    fetchWithAbort(UrlApi + "/tanques/tabs/autoelevadores/inactivos", false);

  // Al montar -> Cargar activos; Al desmontar -> Aborta la carga (si cambio a otro tab se cancela la carga)

  // ------------------------------------------------------------
  // useEffect para cargar datos al montar el componente:
  // - Llama por defecto a obtenerTanques() (activos).
  // - Si el componente se desmonta (ej: cambio de pestaña) se aborta la petición en curso
  // ------------------------------------------------------------

  useEffect(() => {
    obtenerTanques(); // Cargar tanques activos al inicio
    return () => {
      // Aborta la petición actual si existe
      if (abortCtrl.current) abortCtrl.current.abort();
    };
  }, []);

  // ==============================================================
  // ==============================================================

  // DIALOG PARA EDITAR TANQUES
  const [dialogEditar, setDialogEditar] = useState(false);

  // Abrir diálogo
  const handleOpenEditarTanque = () => {
    setDialogEditar(true);
  };

  // Cerrar diálogo
  const handleCloseEditarTanque = () => {
    setDialogEditar(false);
  };

  //*************************************************************************************************************************************/

  // DIALOG PARA VER DATOS DE LOS TANQUES
  const [dialogFicha, setDialogFicha] = useState(false);

  // Abrir diálogo
  const handleOpenDialogFicha = () => {
    setDialogFicha(true);
  };

  // Cerrar diálogo
  const handleCloseDialogFicha = () => {
    setDialogFicha(false);
  };

  //*************************************************************************************************************************************/

  const columns = useMemo(
    () => [
      {
        accessorKey: "nro_serie",
        header: "N° Serie",
        size: 20,
      },
      {
        accessorKey: "capacidad",
        header: "Capacidad (m3)",
        size: 50,
      },
      {
        accessorKey: "sucursal",
        header: "Sucursal",
        size: 40,
      },
      // {
      //   accessorKey: "estado_fisico",
      //   header: "Estado Físico",
      //   size: 60,
      // },
      {
        accessorKey: "fecha_baja",
        header: "Fecha de Baja",
        size: 60,
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          if (!rawDate) return ""; // Si no hay fecha, no mostrar nada
          const date = new Date(rawDate);
          return date.toLocaleDateString("es-AR"); // Muestra como dd/mm/aaaa
        },
      },
      {
        accessorKey: "ultima_prueba",
        header: "Última Prueba",
        size: 80,
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          if (!rawDate) return ""; // Si no hay fecha, no mostrar nada
          const date = new Date(rawDate);
          return date.toLocaleDateString("es-AR"); // Muestra como dd/mm/aaaa
        },
      },
      {
        accessorKey: "ultimo_vencimiento",
        header: "Último Vencimiento",
        size: 80,
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          if (!rawDate) return ""; // Si no hay fecha, no mostrar nada
          const date = new Date(rawDate);
          return date.toLocaleDateString("es-AR"); // Muestra como dd/mm/aaaa
        },
      },
      {
        accessorKey: "fecha_pruebahidraulica",
        header: "Fecha Prueba Hidr.",
        size: 60,
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          if (!rawDate) return ""; // Si no hay fecha, no mostrar nada
          const date = new Date(rawDate);
          return date.toLocaleDateString("es-AR"); // Muestra como dd/mm/aaaa
        },
      },
    ],
    []
  );

  // --------------------------------------------

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "primary",
    size: "small",
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: {
      pageSize: 10,
      pageIndex: 0,
    },
  };

  const tableHeadCellProps = {
    align: "center",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      pt: 1,
      borderColor: "black",
      "& .MuiSvgIcon-root": {
        color: "white", // color de los íconos
      },
    },
  };

  const globalTheme = useTheme();
  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  // ------------------------------------------------------------

  // Función para actualizar los tanques luego de guardar
  const handleTanqueGuardado = () => {
    obtenerTanques(); // Vuelve a obtener los datos actualizados
  };

  return (
    <>
      <Container maxWidth="xl" style={{ marginTop: "-16px" }}>
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            enablePagination={true}
            columns={columns}
            data={autoelevadores ?? []}
            state={{
              isLoading: isLoading,
            }}
            initialState={initialState}
            positionToolbarAlertBanner="bottom"
            enableHiding={false}
            muiTableHeadCellProps={tableHeadCellProps}
            localization={MRT_Localization_ES}
            enableExpandAll={false}
            enableGrouping={true}
            enableRowActions
            positionActionsColumn="last"
            enableColumnActions={false}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            enableDensityToggle={false}
            enableColumnFilters={true}
            muiTableHeadCellFilterTextFieldProps={{
              inputProps: {
                autoComplete: "off", // desactiva autocompletado en el filtro por columna
              },
              sx: {
                backgroundColor: "#ffffff", // Fondo blanco limpio
                borderRadius: "4px", // Bordes más redondeados
                fontSize: "0.9rem", // Texto más moderno
                "& .MuiInputBase-input": {
                  color: "#333",
                  paddingLeft: "6px", // Desplaza el texto hacia la derecha
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ccc", // Borde gris claro
                },
              },
            }}
            muiTableBodyCellProps={({ row }) => ({
              align: "center",
              sx: {
                color: "black",
                borderColor: "black",
              },
            })}
            muiTableBodyRowProps={({ table, row }) => {
              const rowIndex = table
                .getRowModel()
                .rows.findIndex((r) => r.id === row.id);
              // Paleta para activos e inactivos
              const activeColors = ["#a4f0a8", "#b0e0b3ff"];
              const inactiveColors = ["#f5bbbbff", "#ecacacff"];
              // Paleta al pasar el mouse por encima de la fila
              const activeHover = "#d1f0d1";
              const inactiveHover = "#f3d4d4ff";

              // ¿La fila está de baja?
              const isInactiveRow = row.original.baja === 1;
              // Selecciona la paleta según row.baja
              const bgColor = isInactiveRow
                ? inactiveColors[rowIndex % 2]
                : activeColors[rowIndex % 2];
              const hoverColor = isInactiveRow ? inactiveHover : activeHover;

              return {
                sx: {
                  backgroundColor: bgColor,
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": isLoading
                    ? {
                        backgroundColor: bgColor + " !important",
                        "& .MuiTableCell-root": {
                          backgroundColor: bgColor + " !important",
                        },
                      }
                    : {
                        backgroundColor: hoverColor,
                        "& .MuiTableCell-root": {
                          backgroundColor: "inherit !important",
                        },
                      },
                },
              };
            }}
            renderTopToolbarCustomActions={() => (
              <>
                <Box>
                  <Typography variant="poster" fontSize={26}>
                    Autoelevadores
                  </Typography>
                </Box>
              </>
            )}
            renderToolbarInternalActions={({ table }) => (
              <>
                {!hiddenmuestrainactivo ? (
                  <Tooltip title="Mostrar Inactivos">
                    <IconButton
                      onClick={() => obtenerInactivos()}
                      color="error"
                    >
                      <BlockIcon color="error" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!hiddenmuestraactivos ? (
                  <Tooltip title="Mostrar Activos" color="success">
                    <IconButton onClick={() => obtenerTanques()}>
                      <PropaneIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}

                {/* ── Toggle de filtros ── */}
                <MRT_ToggleFiltersButton table={table} />

                {/* ── Botón de full‑screen ── */}
                <MRT_FullScreenToggleButton table={table} />
              </>
            )}
            renderRowActionMenuItems={({ closeMenu, row }) => [
              <>
                <MenuItem
                  key={0}
                  onClick={() => {
                    closeMenu();
                    handleOpenDialogFicha();
                    setTanque(row.original);
                  }}
                  sx={{ m: 0 }}
                >
                  <ListItemIcon>
                    <RemoveRedEyeIcon color="primary" />
                  </ListItemIcon>
                  Ver más Información
                </MenuItem>

                <MenuItem
                  key={1}
                  onClick={() => {
                    handleOpenEditarTanque();
                    setTanque(row.original);
                  }}
                  sx={{ m: 0 }}
                >
                  <ListItemIcon>
                    <BorderColorIcon color="primary" />
                  </ListItemIcon>
                  Editar
                </MenuItem>

                <MenuItem
                  disabled={row.original.id_estado === 6}
                  key={2}
                  onClick={() => {
                    closeMenu();
                    setBaja(true);
                    setTanque(row.original);
                  }}
                  sx={{ m: 0 }}
                >
                  <ListItemIcon>
                    <Delete color="error" />
                  </ListItemIcon>
                  Dar de Baja
                </MenuItem>
              </>,
            ]}
          />
        </ThemeProvider>
      </Container>
      {/* DETALLES TANQUE */}
      <Dialog
        open={dialogFicha}
        onClose={handleCloseDialogFicha}
        fullWidth
        maxWidth={false} // DESACTIVA LOS LIMITES DE MUI (xs, sm, md, lg, xl)
        PaperProps={{
          sx: {
            width: "90vw", // 90% del ancho de la ventana
            height: "80vh", // 80% del alto de la ventana
            maxWidth: "none",
            overflow: "hidden",
          },
        }}
      >
        <DatosTanques
          datos={tanque}
          handleCloseDialogFicha={handleCloseDialogFicha}
        />
      </Dialog>
      {/* EDITAR TANQUE */}
      <Dialog
        fullWidth
        maxWidth={"lg"}
        open={dialogEditar}
        onClose={handleCloseEditarTanque}
      >
        <Editartanque
          datos={tanque}
          handleCloseEditarTanque={handleCloseEditarTanque}
          onTanqueGuardado={handleTanqueGuardado}
        />
      </Dialog>
      {/* BAJA DE TANQUE */}
      {baja ? (
        <BajaTanque
          open={baja}
          setOpen={setBaja}
          datos={tanque}
          actualizar={obtenerTanques}
        />
      ) : null}{" "}
    </>
  );
}

export default Autoelevadores;
